import React, { useState, useEffect } from 'react';

import FloatingActionBar, { FloatingActionBarSpacer } from '@components/shared/ui/FloatingActionBar';
import useSmartBack from '@hooks/shared/useSmartBack';
import PageHeader from '@shared/PageHeader';
import { handleApiError, handleNetworkError } from '@utils/apiErrorHandler';
import { Button, Form, Input, App, Spin } from 'antd';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

export default function ProductCategoryEdit({
  productCategoryService,
  role = 'operator'
}) {
  const { t } = useTranslation();
  const { message } = App.useApp();
  const { id } = useParams();
  const isNew = !id;
  const goBack = useSmartBack();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  // 區分「儲存並新增下一筆」的 loading
  const [savingAndAdding, setSavingAndAdding] = useState(false);
  const [initialLoading, setInitialLoading] = useState(!isNew);

  useEffect(() => {
    if (!isNew) {
      loadProductCategory();
    }
  }, [id]);

  const loadProductCategory = async () => {
    setInitialLoading(true);
    try {
      const response = await productCategoryService.get(id);
      if (response.success) {
        form.setFieldsValue(response.data);
      } else {
        handleApiError(response, { t, fallbackMessage: t('common.error.load_failed') });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Load product category error:', error);
    } finally {
      setInitialLoading(false);
    }
  };

  // 核心提交邏輯：呼叫 create/update 並回傳 response
  const submitValues = async (values) => {
    return isNew
      ? await productCategoryService.create(values)
      : await productCategoryService.update(id, values);
  };

  // 主要提交：成功後導向 show 頁
  const handleSubmit = async (values) => {
    setLoading(true);
    try {
      const response = await submitValues(values);

      if (response.success) {
        message.success(t(isNew ? 'common.message.create_success' : 'common.message.update_success'));
        // 導向該筆記錄的 show 頁
        const recordId = response.data?.id || id;
        navigate(`/product_categories/${recordId}`, { replace: true });
      } else {
        handleApiError(response, {
          t,
          fallbackMessage: t(isNew ? 'common.error.create_failed' : 'common.error.update_failed')
        });
      }
    } catch (error) {
      handleNetworkError(error, { t });
      console.error('Submit product category error:', error);
    } finally {
      setLoading(false);
    }
  };

  // 儲存並新增下一筆：僅在新建模式可用，成功後重置表單留在本頁
  const handleSaveAndAddAnother = async () => {
    try {
      const values = await form.validateFields();
      setSavingAndAdding(true);
      try {
        const response = await submitValues(values);

        if (response.success) {
          message.success(t('common.message.create_success'));
          form.resetFields();
        } else {
          handleApiError(response, {
            t,
            fallbackMessage: t('common.error.create_failed')
          });
        }
      } catch (error) {
        handleNetworkError(error, { t });
        console.error('Submit product category error:', error);
      } finally {
        setSavingAndAdding(false);
      }
    } catch {
      // validateFields 失敗會 reject，antd 會自動高亮錯誤欄位
    }
  };

  if (initialLoading) {
    return (
      <div style={{ textAlign: 'center', padding: '50px 0' }}>
        <Spin size="large" />
      </div>
    );
  }

  const headerBasePath = role === 'operator' ? '/operator' : '/admin';

  return (
    <div>
      <PageHeader
        basePath={headerBasePath}
        items={['products', 'products.product_categories', { label: isNew ? t('common.button.create') : t('common.button.edit') }]}
      />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        autoComplete="off"
      >
        <Form.Item
          label={t('shared.product_categories.form.name')}
          name="name"
          rules={[
            { required: true, message: t('shared.product_categories.form.name_required') }
          ]}
        >
          <Input placeholder={t('shared.product_categories.form.name_placeholder')} />
        </Form.Item>

        <FloatingActionBarSpacer />
      </Form>

      <FloatingActionBar
        role={role}
        onCancel={goBack}
        onSubmit={() => form.submit()}
        loading={loading}
        disabled={savingAndAdding}
        extraButtons={isNew ? (
          <Button
            loading={savingAndAdding}
            disabled={loading}
            onClick={handleSaveAndAddAnother}
          >
            {t('common.button.save_and_add_another')}
          </Button>
        ) : null}
        submitText={t(isNew ? 'common.button.create' : 'common.button.save')}
      />
    </div>
  );
}
